import React, { useCallback, useEffect, useState } from 'react';
import { canReadClaims } from '../utils/roles';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../contexts/authContext';
import {
  Box,
  Container,
  FormControl,
  Select,
  MenuItem,
  Button,
  Checkbox,
  Fab,
} from '@material-ui/core';
import TenantSelector from '../components/TenantSelector';
import { makeStyles } from '@material-ui/core/styles';
import { MDTypography } from '../components/MDTypography';
import {
  getAllBills,
  submitBillVerdict,
  bulkApprovedBills,
  BillWithClaim,
} from '../services/billsService';
import { BillVerdictStatus, Severity } from '../types';
import useSnackbar from '../hooks/useSnackbar';
import { FFTable, FFTableColumn } from '../components/FFTable';
import { MDButton } from 'components/MDButton';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ContextPanel } from '../components/ContextPanel';
import { BillDetailsView } from './Claims/BillDetailsView';
import { BulkDownloadDialog } from '../components/BulkDownload/BulkDownloadDialog';
import { BulkDiscountPdfDialog } from '../components/BulkDiscountPdf/BulkDiscountPdfDialog';
import {
  BulkDownloadConfig,
  BulkDownloadResponse,
  requestBulkDownload,
} from '../services/claimsService';
import { AxiosError } from 'axios';
import { BillActionCell } from './Claims/components';
import {
  BillsFilterBar,
  BillsFilters,
  DEFAULT_BILLS_FILTERS,
} from './Claims/components/BillsFilterBar';

type BillsPageData = {
  entities: BillWithClaim[];
  isLoading: boolean;
  error: null | Error;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

type QueryState = {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const useStyles = makeStyles((theme) => ({
  // Fourteen columns at the shared table's default padding push the Approve and
  // Reject buttons off the right edge, and a reviewer should not have to scroll
  // sideways to act on a bill. The padding is tightened here rather than in
  // FFTable's own stylesheet, which every other screen in the app also uses.
  compactTable: {
    '& .MuiTableCell-root': {
      padding: theme.spacing(0.75, 1),
      fontSize: '0.78rem',
    },
    '& .MuiTableCell-head': {
      fontSize: '0.74rem',
      lineHeight: 1.25,
    },
    // Only the buttons inside cells -- the filter bar's own controls keep
    // their normal size.
    '& .MuiTableCell-root .MuiButton-root': {
      minWidth: 0,
      padding: theme.spacing(0.25, 1),
      fontSize: '0.68rem',
    },
  },
  actionButton: {
    minWidth: 80,
    padding: theme.spacing(0.5, 1),
    fontSize: '0.75rem',
    margin: theme.spacing(0, 0.5),
  },
  approveButton: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
  rejectButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

const mapVerdictStatusToText: Record<BillVerdictStatus, string> = {
  approved: 'Approved',
  'partial-approved': 'Partially Approved',
  rejected: 'Rejected',
  pending: 'Pending',
  processing: 'Processing',
  processed: 'Processed',
  paid: 'Paid',
};

export function BillsPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [inProgress, setInProgress] = useState(false);
  const [pageData, setPageData] = useState<BillsPageData>({
    entities: [],
    isLoading: true,
    error: null,
    pagination: {
      page: 1,
      pageSize: 100,
      total: 0,
      totalPages: 0,
    },
  });
  const [queryState, setQueryState] = useState<QueryState>({
    page: 1,
    pageSize: 100,
    sortBy: 'billedCharges',
    sortOrder: 'desc',
  });
  const [filters, setFilters] = useState<BillsFilters>(DEFAULT_BILLS_FILTERS);
  // Only the very first load replaces the page with a loading message. After
  // that the filter bar must stay mounted, or a search that briefly returns
  // nothing would take the search box -- and the cursor in it -- away.
  const [hasLoaded, setHasLoaded] = useState(false);
  const show = useSnackbar();
  const [currentTenant, setCurrentTenant] = useState<number>(0);
  const [showBulkDownloadDialog, setShowBulkDownloadDialog] = useState(false);

  const [contextPanelOpen, setContextPanelOpen] = useState(false);
  const [billForDetails, setBillForDetails] = useState<BillWithClaim | null>(
    null
  );
  const [isBulkApprovalMode, setIsBulkApprovalMode] = useState(false);
  const [selectedBillIds, setSelectedBillIds] = useState<string[]>([]);
  const [showBulkDiscountPdfDialog, setShowBulkDiscountPdfDialog] = useState(false);
  const [bulkDiscountCarrierId, setBulkDiscountCarrierId] = useState<number>(0);

  const { role } = user || { role: '' };

  const handleSelectOne = (billId: string) => {
    if (selectedBillIds.includes(billId)) {
      setSelectedBillIds((prev) => prev.filter((id) => id !== billId));
    } else {
      setSelectedBillIds((prev) => [...prev, billId]);
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedBillIds(pageData.entities.map((bill) => bill.gemBillNo));
    } else {
      setSelectedBillIds([]);
    }
  };

  const baseColumns: FFTableColumn<BillWithClaim>[] = [
    // {
    //   id: 'dateOfServiceFrom',
    //   label: t('Date of Service'),
    //   type: 'text',
    //   sortable: true,
    //   renderCell: (value) => {
    //     const date = new Date(value);
    //     return date.toLocaleDateString();
    //   },
    // },
    {
      id: 'caseId',
      label: t('Case ID'),
      type: 'text',
      width: 90,
      filterable: true,
    },
    {
      id: 'policyNumber',
      label: t('Policy Number'),
      type: 'text',
      width: 95,
      sortable: true,
      filterable: true,
    },
    {
      id: 'claimType',
      width: 85,
      label: t('Claim Type'),
      type: 'text',
      sortable: true,
      filterable: true,
      renderCell: (value) => {
        return value === 'Outpatient' || value === 'Inpatient'
          ? value
          : 'Other';
      },
    },
    {
      id: 'billType',
      width: 95,
      label: t('Bill Type'),
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      id: 'gemBillNo',
      width: 90,
      label: t('Bill ID'),
      type: 'text',
      filterable: true,
    },
    {
      id: 'payableTo',
      width: 155,
      label: t('Provider Name'),
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      id: 'billedCharges',
      width: 95,
      label: t('Bill Amount'),
      type: 'text',
      sortable: true,
      renderCell: (value) => {
        return <span>{formatter.format(value)}</span>;
      },
    },
    {
      id: 'savingsAmount',
      width: 90,
      label: t('Savings'),
      type: 'text',
      sortable: true,
      renderCell: (value) => {
        return <span>{value ? formatter.format(value) : 'N/A'}</span>;
      },
    },
    {
      id: 'payable',
      width: 90,
      label: t('Allowed'),
      type: 'text',
      sortable: true,
      renderCell: (value) => {
        return <span>{value ? formatter.format(value) : 'N/A'}</span>;
      },
    },
    {
      id: 'costContainmentFees',
      width: 75,
      label: t('Fee'),
      type: 'text',
      sortable: true,
      renderCell: (value) => {
        return <span>{value ? formatter.format(value) : 'N/A'}</span>;
      },
    },
    {
      id: 'totalDue',
      width: 90,
      label: t('Total Due'),
      type: 'text',
      sortable: true,
      renderCell: (value) => {
        return <span>{value ? formatter.format(value) : 'N/A'}</span>;
      },
    },
    {
      type: 'custom',
      id: 'detailsAction',
      label: t('Details'),
      width: 78,
      renderCell: (value, row) => (
        <Button
          className={classes.actionButton}
          onClick={() => {
            setBillForDetails(row);
            setContextPanelOpen(true);
          }}
          disabled={inProgress}
          size="small"
          variant="outlined"
          color="primary"
        >
          {t('Details')}
        </Button>
      ),
    },
    {
      type: 'custom',
      id: 'medicalRecordsStatus',
      label: t('Medical Documents Status'),
      width: 85,
      renderCell: (value, row) => (
        <span>
          {row.Claims?.hipaaWaiverUrl && row.Claims?.medicalRecords?.length > 0
            ? t('Available')
            : t('N/A')}
        </span>
      ),
    },
    {
      type: 'custom' as const,
      id: 'approveRejectAction',
      label: t('Action'),
      width: 145,
      renderCell: (value: any, row: BillWithClaim) => (
        <BillActionCell
          bill={{
            ...row,
            product: row.claimType,
            claimDate: row.createdAt,
            complaint: '',
            dateOfServiceFrom: row.createdAt,
            dateOfServiceTo: row.createdAt,
            billCreateDate: row.createdAt,
            billStatus: row.verdict?.verdict || 'Pending',
            billStatusExplanation: row.verdict?.reason || null,
            billReferenceNo: row.gemBillNo,
            gemClaimNo: row.claimId,
            ClaimsBillsServiceLines: row.ClaimsBillsServiceLines || [],
          }}
          claimId={row.claimId}
          loading={inProgress}
          onVerdictUpdate={handleBillVerdict}
        />
      ),
    },
  ];

  let columns = baseColumns;
  if (isBulkApprovalMode) {
    columns = [
      {
        id: 'select',
        label: '',
        type: 'custom',
        renderCell: (value, row) => (
          <Checkbox
            checked={selectedBillIds.includes(row.gemBillNo)}
            onChange={() => handleSelectOne(row.gemBillNo)}
            color="primary"
          />
        ),
      },
      ...baseColumns.filter((col) => col.id !== 'approveRejectAction'),
    ];
  }

  const fetchDataWithQuery = useCallback(
    async (query: QueryState) => {
      try {
        setPageData((prev) => ({ ...prev, isLoading: true }));
        const response = await getAllBills(
          {
            page: query.page,
            pageSize: query.pageSize,
            sortBy: query.sortBy,
            sortOrder: query.sortOrder,
            fromDate: filters.fromDate,
            untilDate: filters.untilDate,
            status: filters.status || undefined,
            search: filters.search || undefined,
            claimType: filters.claimType || undefined,
            billType: filters.billType || undefined,
          },
          currentTenant
        );

        setPageData({
          entities: response.bills,
          isLoading: false,
          error: null,
          pagination: response.pagination,
        });
        setHasLoaded(true);

        setQueryState(query);
      } catch (error) {
        const e = error as Error;
        setPageData((prev) => ({
          ...prev,
          isLoading: false,
          error: e,
        }));
        show('Failed to load bills data', Severity.error);
      }
    },
    [show, filters, currentTenant]
  );

  // Any change to what is being asked for starts again from the first page.
  // Keyed on the inputs, not on fetchDataWithQuery itself: `show` from the
  // snackbar context is a new function on every render of its provider, so
  // depending on the callback would reload after every message -- and an
  // error message would reload forever.
  useEffect(() => {
    fetchDataWithQuery({ ...queryState, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentTenant]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const newQuery: QueryState = {
        ...queryState,
        page: newPage,
      };
      fetchDataWithQuery(newQuery);
    },
    [queryState, fetchDataWithQuery]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      const newQuery: QueryState = {
        ...queryState,
        page: 1,
        pageSize: newPageSize,
      };
      fetchDataWithQuery(newQuery);
    },
    [queryState, fetchDataWithQuery]
  );

  const onActionClickHandler = useCallback(
    async (billId: string | number, action: string) => {
      const bill = pageData.entities.find((b) => b.id === Number(billId));
      if (!bill) return;

      if (action === 'detailsAction') {
        setBillForDetails(bill);
        setContextPanelOpen(true);
      }
    },
    [pageData.entities]
  );

  const handleBillVerdict = useCallback(
    async (
      billId: number,
      verdict: BillVerdictStatus,
      amount: number,
      reason: string
    ) => {
      try {
        setInProgress(true);

        const bill = pageData.entities.find((b) => b.id === billId);
        if (!bill) {
          show('Bill not found', Severity.error);
          return;
        }

        await submitBillVerdict(bill.claimId, {
          verdict,
          amount,
          billId: bill.gemBillNo,
          reason,
        });

        let actionText = '';
        switch (verdict) {
          case 'approved':
            actionText = 'Bill Approved';
            break;
          case 'partial-approved':
            actionText = 'Bill Partially Approved';
            break;
          case 'rejected':
            actionText = 'Bill Rejected';
            break;
          case 'pending':
            actionText = 'Bill Verdict Cancelled';
            break;
        }

        show(actionText, Severity.success);

        await fetchDataWithQuery(queryState);
      } catch (error) {
        const actionText = verdict === 'rejected' ? 'Reject' : 'Approve';
        show(`${actionText} Bill Failed`, Severity.error);
      } finally {
        setInProgress(false);
      }
    },
    [show, queryState, fetchDataWithQuery, pageData.entities]
  );

  const handleDownloadExcel = async () => {
    // The export is what is on screen: same search, filters and tenant.
    const response = await getAllBills(
      {
        page: 1,
        pageSize: 10000,
        fromDate: filters.fromDate,
        untilDate: filters.untilDate,
        status: filters.status || undefined,
        search: filters.search || undefined,
        claimType: filters.claimType || undefined,
        billType: filters.billType || undefined,
      },
      currentTenant
    );
    //   id: 'dateOfServiceFrom',
    //   label: t('Date of Service'),
    const headers = [
      { id: 'dateOfServiceFrom', label: 'Date of Service' },
      ...columns,
      { id: 'verdict', label: 'Status' },
      { id: 'payableTo', label: 'Provider Name' },
    ]
      .filter((x) => x.id !== 'detailsAction' && x.id !== 'approveRejectAction')
      .map((col) => ({ id: col.id, label: col.label }));
    const res: any[] = [];
    response.bills.forEach((bill: any) => {
      const row: any = {};
      headers.forEach((header) => {
        if (header.id === 'verdict') {
          row[header.label] = bill[header.id]?.verdict
            ? mapVerdictStatusToText[
                bill[header.id].verdict as BillVerdictStatus
              ]
            : 'Pending';
        } else {
          row[header.label] = bill[header.id];
        }
      });
      res.push(row);
    });
    const worksheet = XLSX.utils.json_to_sheet(res);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bills');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'bills-data.xlsx');
  };

  const handleBulkDownloadClick = () => {
    setShowBulkDownloadDialog(true);
  };

  const handleBulkDownloadClose = () => {
    setShowBulkDownloadDialog(false);
  };

  const handleBulkDownloadSubmit = async (
    config: BulkDownloadConfig
  ): Promise<BulkDownloadResponse> => {
    try {
      const response = await requestBulkDownload(config);
      show(
        'Bulk download completed successfully! Download will start automatically.',
        Severity.success
      );
      return response;
    } catch (error) {
      const errors =
        error instanceof AxiosError ? error.response?.data?.errors || [] : [];
      const errorMessage = errors
        .map((x: { message: string }) => x.message)
        .join(', ');
      show(errorMessage, Severity.error);
      throw new Error(errorMessage);
    }
  };

  const handleBulkApprovalToggle = () => {
    const newMode = !isBulkApprovalMode;
    setIsBulkApprovalMode(newMode);
    setSelectedBillIds([]);
    // Only open bills can be approved in bulk, so entering the mode pins the
    // status to open. The reload that follows starts from page 1.
    if (newMode && filters.status !== 'open') {
      setFilters((prev) => ({ ...prev, status: 'open' }));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedBillIds.length === 0) return;

    try {
      setInProgress(true);
      await bulkApprovedBills(selectedBillIds);
      show(`${selectedBillIds.length} bills approved successfully`, Severity.success);
      setSelectedBillIds([]);
      setIsBulkApprovalMode(false);
      fetchDataWithQuery(queryState);
    } catch (error) {
      show('Failed to approve bills', Severity.error);
    } finally {
      setInProgress(false);
    }
  };

  const canAccessBulkDownload = canReadClaims(role);

  const handleBulkDiscountPdfClick = () => {
    const selectedBills = pageData.entities.filter((bill) =>
      selectedBillIds.includes(bill.gemBillNo)
    );

    const carrierIds = new Set(
      selectedBills
        .map((bill) => bill.Claims?.member?.carrier)
        .filter((id): id is number => id !== undefined && id !== null)
    );

    if (carrierIds.size !== 1) {
      show(t('bulkDiscountPdf.mixedCarriersError'), Severity.error);
      return;
    }

    const carrierId = [...carrierIds][0];
    setBulkDiscountCarrierId(carrierId);
    setShowBulkDiscountPdfDialog(true);
  };

  if (!user) {
    return <div>{t('Loading')}..</div>;
  }

  if (user.status === 'new') {
    return (
      <Container>
        <MDTypography variant="h4" component="p">
          Welcome to FairFare Dashboard!
        </MDTypography>
        <MDTypography component="p">
          To activate your account and access all FairFare features please
          contact us
        </MDTypography>
      </Container>
    );
  }

  if (!hasLoaded && pageData.isLoading) {
    return <div>{t('Loading')}..</div>;
  }

  if (pageData.error) {
    return (
      <div>
        {t('Error loading bills data')}: {pageData.error.message}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '100vw', margin: '0 auto', overflow: 'auto' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box display="flex" alignItems="center" style={{ gap: '16px' }}>
          <MDButton
            variant={isBulkApprovalMode ? 'outlined' : 'gradient'}
            color={isBulkApprovalMode ? 'info' : 'info'}
            onClick={handleBulkApprovalToggle}
          >
            {isBulkApprovalMode ? t('Cancel Bulk Approval') : t('Bulk Approval')}
          </MDButton>

        </Box>

        <Box display="flex" justifyContent="flex-end" style={{ gap: '8px' }}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleDownloadExcel}
          >
            {t('Download as Excel')}
          </MDButton>
          {canAccessBulkDownload && (
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleBulkDownloadClick}
            >
              {t('Bills Bulk Download')}
            </MDButton>
          )}
          {canAccessBulkDownload && selectedBillIds.length > 0 && (
            <MDButton
              variant="gradient"
              color="primary"
              onClick={handleBulkDiscountPdfClick}
            >
              {t('bulkDiscountPdf.buttonLabel')}
            </MDButton>
          )}
        </Box>
      </Box>

      <BillsFilterBar
        filters={filters}
        onChange={setFilters}
        total={pageData.pagination.total}
        statusLocked={isBulkApprovalMode}
        tenantSlot={
          role === 'super_user' ? (
            <TenantSelector
              value={currentTenant}
              onChange={setCurrentTenant}
              label={t('Tenant')}
              compact
            />
          ) : undefined
        }
      />
      <Box className={classes.compactTable}>
      <FFTable
        columns={columns}
        rows={pageData.entities}
        onActionClick={onActionClickHandler}
        pagination={{
          page: pageData.pagination.page,
          pageSize: pageData.pagination.pageSize,
          total: pageData.pagination.total,
          totalPages: pageData.pagination.totalPages,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: [50, 100, 200],
        }}
        serverSide={true}
        loading={pageData.isLoading}
        error={pageData.error}
        onSortChange={(orderBy, order) => {
          const newQuery: QueryState = {
            ...queryState,
            page: 1,
            sortBy: orderBy,
            sortOrder: order,
          };

          fetchDataWithQuery(newQuery);
        }}
        initialSort={{
          orderBy: queryState.sortBy || 'billedCharges',
          order: queryState.sortOrder || 'desc',
        }}
        rowKey="id"
        emptyStateMessage={t('No bills records found')}
        errorStateMessage={t('Error loading bills data')}
        loadingStateMessage={t('Loading bills data...')}
      />
      </Box>

      <ContextPanel
        open={contextPanelOpen}
        onClose={() => {
          setContextPanelOpen(false);
          setBillForDetails(null);
        }}
        side="right"
        size="large"
        title={
          billForDetails
            ? `${t('Bill Details')} - ${billForDetails.gemBillNo}`
            : t('Bill Details')
        }
      >
        {billForDetails && (
          <BillDetailsView
            bill={{
              ...billForDetails,
              billReferenceNo: billForDetails.gemBillNo,
              claimDate: billForDetails.dateOfServiceFrom,
              complaint: '',
              dateOfServiceFrom: billForDetails.dateOfServiceFrom,
              dateOfServiceTo: billForDetails.dateOfServiceTo,
              billCreateDate: billForDetails.createdAt,
              billStatus: billForDetails.verdict?.verdict || 'Pending',
              billStatusExplanation: billForDetails.verdict?.reason || 'N/A',
              gemClaimNo: billForDetails.claimId,
              product: billForDetails.claimType,
              ClaimsBillsServiceLines:
                billForDetails.ClaimsBillsServiceLines || [],
              hipaaWaivers: {
                hipaaWaiverUrls: billForDetails.Claims?.hipaaWaiverUrl
                  ? [billForDetails.Claims.hipaaWaiverUrl]
                  : [],
                medicalRecords: billForDetails.Claims?.medicalRecords || [],
              },
            }}
          />
        )}
      </ContextPanel>

      <BulkDownloadDialog
        open={showBulkDownloadDialog}
        onClose={handleBulkDownloadClose}
        onDownload={handleBulkDownloadSubmit}
      />

      <BulkDiscountPdfDialog
        open={showBulkDiscountPdfDialog}
        onClose={() => setShowBulkDiscountPdfDialog(false)}
        selectedBillIds={selectedBillIds}
        carrierId={bulkDiscountCarrierId}
      />
      {/* Floating CTA for Bulk Approval */}
      {isBulkApprovalMode && selectedBillIds.length > 0 && (
        <Box
          style={{
            position: 'fixed',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
          }}
        >
          <Fab
            variant="extended"
            color="primary"
            onClick={handleBulkApprove}
            disabled={inProgress}
          >
            {t('Approve {{count}} Bills', { count: selectedBillIds.length })}
          </Fab>
        </Box>
      )}
    </div>
  );
}
